// src/components/ContactModal.js
// Contact options that work for every visitor: copy the address, Gmail, Outlook, or the device's mail app.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { mabrookEmail } from "../utils/constants";
import "../styles/ContactModal.css";
import { createPortal } from "react-dom";

export default function ContactModal({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const isRTL = i18n.language?.startsWith("ar");

  useEffect(() => {
    if (!open) {
      setCopied(false);
      return;
    }
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(mabrookEmail);
    } catch {
      const el = document.createElement("textarea");
      el.value = mabrookEmail;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const options = [
    {
      label: "Gmail",
      href: `https://mail.google.com/mail/?view=cm&to=${mabrookEmail}`,
    },
    {
      label: "Outlook",
      href: `https://outlook.live.com/mail/0/deeplink/compose?to=${mabrookEmail}`,
    },
  ];

  return createPortal(
    <div className="contact-modal-backdrop" onClick={onClose}>
      <div
        className="contact-modal"
        dir={isRTL ? "rtl" : "ltr"}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
      >
        <button
          className="contact-modal-close"
          onClick={onClose}
          aria-label={t("Close")}
        >
          ×
        </button>

        <h3 id="contact-modal-title" className="contact-modal-title">
          {t("Contact Us")}
        </h3>
        <p className="contact-modal-text">{t("Write to us at")}</p>

        <div className="contact-modal-email-row">
          <span className="contact-modal-email" dir="ltr">
            {mabrookEmail}
          </span>

          <button
            type="button"
            className={`contact-modal-copy${copied ? " is-copied" : ""}`}
            onClick={copy}
          >
            {copied ? `✓ ${t("Copied")}` : t("Copy email address")}
          </button>
        </div>

        <div className="contact-modal-divider">
          <span>{t("Or open")}</span>
        </div>

        <div className="contact-modal-options">
          {options.map((o) => (
            <a
              key={o.label}
              href={o.href}
              className="contact-modal-option"
              target="_blank"
              rel="noopener noreferrer"
            >
              {o.label}
            </a>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
